// eslint-disable-next-line no-unused-vars
import React from 'react';
import { object, node, func, bool, array } from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Button, Loading, SkeletonText } from '@carbon/react';
import { Truck, Info } from 'lucide-react';

import FeedbackForm from 'Containers/FeedbackForm/FeedbackForm';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import { NWNotification, NWOrderTypeBadge } from 'Components/shared';
import usePermissions from 'Hooks/usePermissions';

const StyledSuccessCheckoutPage = styled.div`
  .topBar {
    display: flex;
    align-content: center;
    justify-content: space-between;
    margin: 20px 0 20px;
  }

  .orderTitle {
    display: flex;
    align-items: center;
    font-size: 1.875rem;
    font-weight: 600;
    line-height: 2.25rem;
    color: ${ props => props.theme.colors.darkGray };

    svg { 
      margin-right: 10px;
      width: 27px;
    }

    h1 {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .cds--skeleton__text {
        min-width: 70px;
        max-width: 70px;
      }
      
      > div {
        font-size: 1.875rem;
        margin: 15px 18px 10px 0;
        display: flex;
        gap: 10px;
        align-items: center;
        white-space: nowrap;

        .bx--skeleton__text {
          min-width: 70px;
          max-width: 70px;
        }

        span {
          font-weight: 600;
        }

        + span {
          &.rsvp {
            color: #E8720B;
            font-size: 1rem;
            display: flex;
            align-items: center;
            &:before {
              position: relative;
              top: 2px;
              left: -10px;
              content: ${ () => `url(${Info})` };
            }
          }
          padding: 0 19px;
          margin-top: 5px;
          line-height: 22px;
        }
      }

      .deliveryEstimate {
        font-size: 14px !important;
      }
    }
  }

  .summaryContainer {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 25px;

    .orderSummary {
      background-color: transparent;
      border: 0;
      max-width: 590px;
      min-width: ${ props => props.dropShipment ? '590px' : '330px' };
      .item {
        grid-template-columns: auto 145px;
      }
      p {
        font-size: 1.125rem;
        line-height: 1.35rem;
        text-align: right;
        margin-right: ${ props => props.dropShipment ? '20px' : '10px' };
      }

      .title, hr {
        display: none;
      }

      .total {
        display: grid !important;
        align-items: flex-start !important;
        p {
          font-size: 1.25rem;
          line-height: 1.75rem;
          font-weight: 600;
        }
        span {
          font-size: 1.5rem;
          line-height: 1.75rem;
          margin: 0;
        }
      }
    }
  }

  .actions {
    display: flex;
    gap: 20px;
    align-items: center;
  }

  .generateInvoice {
    display: flex;
    justify-content: center;
    align-content: center;
    height: 40px;
    max-width: 300px;
    min-width: 250px;
    width: 100%;
    padding: 0;
    font-size: 1rem;
    font-weight: normal;
    &:not(:disabled) {
      color: ${ props => props.theme.colors.white };
      background-color: ${ props => props.theme.colors.default };
    }
  }

  .back, .viewOrder {
    display: block;
    padding: 0;
    width: 200px;
    height: 40px;
    border: 1px solid ${ props => props.theme.colors.blue };
    text-align: center;
    font-size: 1rem;
    line-height: 38px;
    color: ${ props => props.theme.colors.blue };
  }

  .back {
    margin: 15px 0 35px;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    h1 {
      flex-direction: column;

      > div {
        flex-direction: column;
      }
    }

    .summaryContainer  {
      .orderSummary {
        p {
          text-align: left;
          margin-right: 0;
        }
      }
    }

    .orderTitle {
      justify-content: center;
      flex-wrap: wrap;
      padding-bottom: 20px;
    }

    .topBar {
      flex-direction: column;
      align-items: center;
    }

    .generateInvoice {
      max-width: unset;
    }

    .actions {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .viewOrder {
      width: 100%;
    }

    .back {
      width: auto;
    }
  }
`;

const SuccessCheckoutComponent = ({
  order,
  children,
  errors,
  loading,
  onGenerateInvoice,
  isFirstOrder,
  isLastOrder,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isExternalReseller } = usePermissions();

  const stockReservation = order?.stockReservation || order?.statusId === 2;

  return (
    <StyledSuccessCheckoutPage theme={ theme } dropShipment={ order.dropShipment } className="grid-container">
      { isFirstOrder && <FeedbackForm orderId={ order.id } /> }
      { !!order?.id && <NWNotification errors={ errors } /> }
      <div className="topBar">
        <div className="orderTitle">
          { order?.dropShipment && <Truck /> }
          <h1>
            <div>
              <span>{ t('order.orderId', { orderId: '' }) }</span>
              { order?.orderNumber || <SkeletonText /> }
              { (order?.items?.length > 0 && !order.dropShipment && !stockReservation) && (
                <NWOrderTypeBadge>
                  { order?.supplierId !== null ? t('products.supplierStock') : t('products.nodStock') }
                </NWOrderTypeBadge>
              ) }
            </div>
            { (order?.items?.length > 0 && !order.dropShipment && !stockReservation) && (
              <DeliveryEstimate
                estimate={ order?.supplierOfferDeliveryTimeDays }
                isExternalSupplier={ !!order?.supplierId }
              />
            ) }
          </h1>
          { !stockReservation && order?.dropShipment && (
            <NWOrderTypeBadge>{ t('order.dropShipmentOrderType') }</NWOrderTypeBadge>
          ) }
          { stockReservation && !order?.dropShipment && (
            <NWOrderTypeBadge>{ t('order.stockReservation') }</NWOrderTypeBadge>
          ) }
        </div>
        <div className="actions">
          <Link
            to={ `/orders/${ order?.id }` }
            className="viewOrder"
          >
            { t('orders.viewOrder') }
          </Link>
          { !stockReservation && !isExternalReseller && (
            <Button
              className="generateInvoice"
              kind="danger"
              onClick={ onGenerateInvoice }
              data-test="onGenerateInvoice"
              data-testid={ order.id }
              disabled={ loading }
            >
              { loading && <Loading active withOverlay={ false } small /> }
              { t('order.generateProformInvoice') }
            </Button>
          ) }
        </div>
      </div>
      { children }
      { isLastOrder && (
        <Link
          to="/products"
          className="back"
        >
          { '<' } { t('common.backTo', { label: t('products.products') }) }
        </Link>
      ) }
    </StyledSuccessCheckoutPage>
  );
};

SuccessCheckoutComponent.propTypes = {
  children: node,
  order: object,
  errors: array,
  loading: bool,
  onGenerateInvoice: func,
  isFirstOrder: bool,
  isLastOrder: bool,
};
SuccessCheckoutComponent.displayName = 'SuccessCheckoutComponent';

export default SuccessCheckoutComponent;
